import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Archive, FileText, RefreshCw, Trash2, Upload, User } from 'lucide-react';
import type { DashboardContainer, DepotCheckResponse, DepotOption, StatusFlag } from '@ctp/shared';
import { useAuth } from '../auth/AuthContext';
import { ApiError, billingApi, containersApi, depotsApi, type BillingStatus } from '../lib/api';
import { ExcelImportDialog } from '../components/ExcelImportDialog';
import { PdfImportDialog } from '../components/PdfImportDialog';
import { DepotCell } from '../components/DepotCell';
import { PremiumTable } from '../components/PremiumTable';
import { ArchiveTable } from '../components/ArchiveTable';
import { PremiumActivateButton } from '../components/PremiumActivateButton';
import { detectFlashedIds, partitionByPlan, refreshedAtMap } from '../lib/premium';
import { KpiCard } from '../components/KpiCard';
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  EmptyState,
  Input,
  Modal,
  PageLoader,
  Spinner,
  Table,
  TBody,
  TD,
  TH,
  THead,
  TR,
} from '../components/ui';

const DAY_MS = 86_400_000;

/** Initialen aus einem Anzeigenamen (erste Buchstaben des ersten + letzten Worts) für den Badge. */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const first = parts[0] ?? '';
  const last = parts.length > 1 ? (parts[parts.length - 1] ?? '') : '';
  const two = (first.charAt(0) + last.charAt(0)).toUpperCase();
  return two.length === 2 ? two : first.slice(0, 2).toUpperCase() || '—';
}

/** Sehr kompakte Status-Pille (ein Buchstabe, farbcodiert) — hält die Zeile niedrig; Tooltip = Klartext. */
function StatusChip({
  letter,
  title,
  flag,
}: {
  letter: string;
  title: string;
  flag: StatusFlag;
}): JSX.Element {
  const cls =
    flag === 'ok'
      ? 'bg-status-ok/15 text-status-ok'
      : flag === 'problem'
        ? 'bg-status-problem/15 text-status-problem'
        : 'bg-status-unknown/15 text-status-unknown';
  return (
    <span
      title={title}
      className={`inline-flex h-5 w-5 items-center justify-center rounded text-[10px] font-semibold ${cls}`}
    >
      {letter}
    </span>
  );
}

/** Kompaktes Filter-Eingabefeld in der Kopf-Filterzeile (eine Spalte). */
function FilterInput({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}): JSX.Element {
  return (
    <Input
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className="h-7 px-2 text-xs font-normal"
    />
  );
}

/**
 * Dashboard (ADR-021/ADR-027/ADR-034): KPIs → Ausnahmen → gespeicherte Container. Lite speichert
 * jede Abfrage und zeigt sie hier; **kein** Auto-Update — pro Zeile kann manuell aktualisiert werden
 * (zählt gegen das Limit; bei vollem Kontingent blockt der Server mit 402).
 */
export function DashboardPage(): JSX.Element {
  const { t, i18n } = useTranslation();
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'org_admin' || user?.role === 'super_admin';

  const [containers, setContainers] = useState<DashboardContainer[]>([]);
  // Reiter „Aktiv" (Default) / „Abgeschlossen" (Archiv, EPIC Q). Der Archiv-Reiter hat einen
  // eigenen Fetch (?state=completed) und nimmt NICHT am 45-s-Polling/Flash teil (A-122).
  const [tab, setTab] = useState<'active' | 'completed'>('active');
  const [archived, setArchived] = useState<DashboardContainer[]>([]);
  const [archiveLoading, setArchiveLoading] = useState(false);
  const [reactivatingId, setReactivatingId] = useState<string | null>(null);
  const [completingId, setCompletingId] = useState<string | null>(null);
  // Auto-abgeschlossene Reise: Reaktivieren = NEUE Abfrage (Bestätigungsdialog mit Kosten-Hinweis).
  const [pendingReactivate, setPendingReactivate] = useState<DashboardContainer | null>(null);
  const [billing, setBilling] = useState<BillingStatus | null>(null);
  // Flash-Erkennung (D7): Snapshot der lastRefreshedAt-Werte + aktuell aufleuchtende Premium-Zeilen.
  const prevRefreshed = useRef<Map<string, string | null>>(new Map());
  const [flashedIds, setFlashedIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [gate, setGate] = useState<string | null>(null);
  const [notice, setNotice] = useState('');
  const [refreshingId, setRefreshingId] = useState<string | null>(null);
  const [importOpen, setImportOpen] = useState(false);
  const [pdfImportOpen, setPdfImportOpen] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<DashboardContainer | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  // Leerdepot-Prüfung je Zeile (EPIC I): Dropdown-Liste + Auswahl/Ergebnis/Lauf-Status pro Container.
  const [depots, setDepots] = useState<DepotOption[]>([]);
  const [depotSel, setDepotSel] = useState<Record<string, string>>({});
  const [depotResult, setDepotResult] = useState<Record<string, DepotCheckResponse>>({});
  const [depotChecking, setDepotChecking] = useState<Record<string, boolean>>({});
  // Anzeige-Filter: „nur meine" + Spaltenfilter (Teilstring, case-insensitive).
  const [onlyMine, setOnlyMine] = useState(false);
  const [filters, setFilters] = useState({
    number: '',
    carrier: '',
    vesselPort: '',
    eta: '',
    updated: '',
    createdBy: '',
  });

  /** Lösch-Recht: eigene Container immer, Org-/Super-Admin alle der Org. */
  const canDelete = (c: DashboardContainer): boolean =>
    isAdmin || (!!user && c.createdByUserId === user.id);

  const fmtDate = useCallback(
    (iso: string | null): string => {
      if (!iso) return '—';
      const d = new Date(iso);
      return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString(i18n.language);
    },
    [i18n.language],
  );

  const loadBilling = useCallback(async (): Promise<void> => {
    try {
      setBilling(await billingApi.status(token));
    } catch {
      /* Billing-Hinweis ist optional — Dashboard funktioniert auch ohne. */
    }
  }, [token]);

  /** Container + Verbrauch neu laden (z. B. nach dem Excel-Import). */
  const reloadAll = useCallback(async (): Promise<void> => {
    try {
      const [list, status] = await Promise.all([
        containersApi.list(token),
        billingApi.status(token).catch(() => null),
      ]);
      prevRefreshed.current = refreshedAtMap(list); // kein Flash für einen bewussten Reload
      setContainers(list);
      setBilling(status);
    } catch {
      /* still: Dashboard zeigt den vorherigen Stand. */
    }
  }, [token]);

  /** Archiv-Reiter laden (eigener Fetch, kein Polling — A-122). */
  const loadArchived = useCallback(async (): Promise<void> => {
    setArchiveLoading(true);
    try {
      setArchived(await containersApi.list(token, 'completed'));
    } catch {
      /* still: Archiv zeigt den vorherigen Stand. */
    } finally {
      setArchiveLoading(false);
    }
  }, [token]);

  // Beim Wechsel auf den Archiv-Reiter frisch laden.
  useEffect(() => {
    if (tab === 'completed') void loadArchived();
  }, [tab, loadArchived]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      containersApi.list(token),
      billingApi.status(token).catch(() => null),
      depotsApi.list(token).catch(() => [] as DepotOption[]),
    ])
      .then(([list, status, depotList]) => {
        if (cancelled) return;
        prevRefreshed.current = refreshedAtMap(list);
        setContainers(list);
        setBilling(status);
        setDepots(depotList);
      })
      .catch(
        (err) =>
          !cancelled && setError(err instanceof ApiError ? err.message : t('dashboard.loadError')),
      )
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [token, t]);

  // Periodisches Polling (D7): rückt für einen Premium-Container lastRefreshedAt vor → Zeile flasht ~2 s.
  useEffect(() => {
    const POLL_MS = 45_000;
    const timer = setInterval(() => {
      void containersApi
        .list(token)
        .then((list) => {
          const flashed = detectFlashedIds(prevRefreshed.current, list);
          prevRefreshed.current = refreshedAtMap(list);
          setContainers(list);
          if (flashed.length > 0) {
            setFlashedIds(new Set(flashed));
            setTimeout(() => setFlashedIds(new Set()), 2100);
          }
        })
        .catch(() => undefined);
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [token]);

  async function onDelete(c: DashboardContainer): Promise<void> {
    setDeletingId(c.trackedContainerId);
    setError('');
    try {
      await containersApi.remove(c.trackedContainerId, token);
      setContainers((prev) => prev.filter((x) => x.trackedContainerId !== c.trackedContainerId));
      setArchived((prev) => prev.filter((x) => x.trackedContainerId !== c.trackedContainerId));
      setPendingDelete(null);
      await loadBilling();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('dashboard.deleteError'));
      setPendingDelete(null);
    } finally {
      setDeletingId(null);
    }
  }

  /** Manueller Abschluss (EPIC Q / A-121): archiviert die Org-Sicht — Zeile wandert ins Archiv. */
  async function onCompleteManual(c: DashboardContainer): Promise<void> {
    setCompletingId(c.trackedContainerId);
    setError('');
    try {
      await containersApi.complete(c.trackedContainerId, token);
      await reloadAll();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('archive.completeError'));
    } finally {
      setCompletingId(null);
    }
  }

  /**
   * Reaktivieren aus dem Archiv (EPIC Q): manuell archiviert → Unarchive + zurück in „Aktiv";
   * auto-abgeschlossene Reise → Bestätigungsdialog „Neue Abfrage starten?" (Zählung/Preis-Hinweis),
   * dann bestehender Track-/Premium-Flow über die Abfrage-Seite.
   */
  async function onReactivate(c: DashboardContainer): Promise<void> {
    if (c.journeyCompletedAt) {
      setPendingReactivate(c);
      return;
    }
    setReactivatingId(c.trackedContainerId);
    setError('');
    try {
      await containersApi.reactivate(c.trackedContainerId, token);
      await Promise.all([reloadAll(), loadArchived()]);
    } catch (err) {
      if (err instanceof ApiError && err.code === 'journey_completed') {
        // Race: Reise wurde zwischenzeitlich auto-abgeschlossen → Neu-Abfrage anbieten.
        setPendingReactivate(c);
      } else {
        setError(err instanceof ApiError ? err.message : t('archive.reactivateError'));
      }
    } finally {
      setReactivatingId(null);
    }
  }

  /** Avis-Prüfung einer Zeile am gewählten Leerdepot (sofort bei Auswahl / beim Aktualisieren). */
  async function runDepotCheck(
    containerId: string,
    containerNumber: string,
    depotId: string,
    reference?: string,
  ): Promise<void> {
    setDepotSel((p) => ({ ...p, [containerId]: depotId }));
    if (!depotId) {
      setDepotResult((p) => {
        const next = { ...p };
        delete next[containerId];
        return next;
      });
      return;
    }
    const depot = depots.find((d) => d.id === depotId);
    // I2c: Depots ohne Online-Auskunft → keine Prüfung (Zelle zeigt „nicht prüfbar").
    // I2b: referenzpflichtige Depots (IGS/HCS/…) warten aufs Referenzfeld (Enter) statt sofort zu prüfen.
    if (depot?.integrationType === 'none' || (depot?.requiresReference && !reference)) {
      setDepotResult((p) => {
        const next = { ...p };
        delete next[containerId];
        return next;
      });
      return;
    }
    setDepotChecking((p) => ({ ...p, [containerId]: true }));
    try {
      // trackedContainerId mitgeben → bei Premium-Zeilen persistiert der Server die Auswahl für den
      // periodischen Recheck (P7). Bei Lite ignoriert der Server sie (kein Auto-Recheck).
      const res = await depotsApi.check(containerNumber, depotId, token, containerId, reference);
      setDepotResult((p) => ({ ...p, [containerId]: res }));
    } catch (err) {
      setDepotResult((p) => ({
        ...p,
        [containerId]: {
          depotId,
          depotName: '',
          outcome: 'error',
          message: err instanceof ApiError ? err.message : t('dashboard.depot.error'),
        },
      }));
    } finally {
      setDepotChecking((p) => ({ ...p, [containerId]: false }));
    }
  }

  async function onRefresh(id: string): Promise<void> {
    setRefreshingId(id);
    setError('');
    setGate(null);
    setNotice('');
    try {
      const res = await containersApi.refresh(id, token);
      // Abgeschlossene Reise (EPIC Q): kein Fetch, keine Zählung — Zeile wandert ins Archiv.
      if (res.outcome === 'completed') {
        setNotice(t('archive.refreshBlocked'));
        await reloadAll();
        return;
      }
      setContainers((prev) => prev.map((c) => (c.trackedContainerId === id ? res.container : c)));
      // Keine Live-Quelle angebunden → Daten bewusst unverändert, keine Abfrage verbraucht.
      if (res.outcome === 'no_source') setNotice(t('dashboard.noSource'));
      // „Aktualisieren" prüft auch das gewählte Leerdepot erneut (warten bis Anmeldung vorliegt → erneut prüfen).
      const selDepot = depotSel[id];
      if (selDepot) void runDepotCheck(id, res.container.containerNumber, selDepot);
      await loadBilling(); // Verbrauchsanzeige aktualisieren
    } catch (err) {
      if (err instanceof ApiError && err.httpStatus === 402)
        setGate(err.code ?? 'usage_limit_reached');
      else setError(err instanceof ApiError ? err.message : t('dashboard.refreshError'));
    } finally {
      setRefreshingId(null);
    }
  }

  const kpis = useMemo(() => {
    const now = Date.now();
    return {
      active: containers.length,
      etaWeek: containers.filter((c) => {
        if (!c.eta) return false;
        const diff = new Date(c.eta).getTime() - now;
        return diff >= 0 && diff <= 7 * DAY_MS;
      }).length,
      pickup: containers.filter((c) => c.status.pickup === 'ok').length,
    };
  }, [containers]);

  /** „X von Y Abfragen genutzt" bzw. Trial-Hinweis. */
  const usageHint = useMemo(() => {
    if (!billing) return null;
    if (billing.hasActivePlan && billing.limit != null) {
      return t('dashboard.usagePlan', { used: billing.used, limit: billing.limit });
    }
    return t('dashboard.usageTrial', {
      remaining: billing.trialRemaining,
      quota: billing.trialQuota,
    });
  }, [billing, t]);

  // Zwei Tabellen (D7): Premium oben, Standard unten. KPIs/Filter bleiben auf der Gesamtmenge.
  const { premium, standard } = useMemo(() => partitionByPlan(containers), [containers]);

  /** Sichtbare Standard-Zeilen: „nur meine" + Spaltenfilter (Teilstring). Premium läuft separat. */
  const visibleContainers = useMemo(() => {
    const inc = (hay: string, needle: string): boolean =>
      hay.toLowerCase().includes(needle.trim().toLowerCase());
    return standard.filter((c) => {
      if (onlyMine && c.createdByUserId !== user?.id) return false;
      if (filters.number && !inc(c.containerNumber, filters.number)) return false;
      if (filters.carrier && !inc(`${c.carrierName ?? ''} ${c.carrierCode ?? ''}`, filters.carrier))
        return false;
      if (filters.vesselPort && !inc(`${c.vessel ?? ''} ${c.port ?? ''}`, filters.vesselPort))
        return false;
      if (filters.eta && !inc(fmtDate(c.eta), filters.eta)) return false;
      if (filters.updated && !inc(fmtDate(c.lastRefreshedAt), filters.updated)) return false;
      if (filters.createdBy && !inc(c.createdByName ?? '', filters.createdBy)) return false;
      return true;
    });
  }, [standard, onlyMine, filters, user?.id, fmtDate]);

  const premiumMode = billing?.premiumBillingMode ?? 'wallet';

  if (loading) return <PageLoader />;

  return (
    <div className="space-y-8">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">{t('dashboard.title')}</h1>
          <p className="text-muted-foreground">
            {t('dashboard.welcome', { email: user?.email ?? '' })}
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="secondary" onClick={() => setImportOpen(true)}>
            <Upload className="h-4 w-4" />
            {t('import.button')}
          </Button>
          <Button variant="secondary" onClick={() => setPdfImportOpen(true)}>
            <FileText className="h-4 w-4" />
            {t('pdfImport.button')}
          </Button>
          <Link to="/tracking">
            <Button>{t('dashboard.startTracking')}</Button>
          </Link>
        </div>
      </div>

      <ExcelImportDialog
        open={importOpen}
        onClose={() => setImportOpen(false)}
        onImported={() => void reloadAll()}
        token={token}
      />

      <PdfImportDialog
        open={pdfImportOpen}
        onClose={() => setPdfImportOpen(false)}
        onImported={() => void reloadAll()}
        token={token}
      />

      {error && <Alert kind="error">{error}</Alert>}
      {notice && <Alert kind="info">{notice}</Alert>}
      {gate && (
        <Alert kind="warn">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span>
              {gate === 'trial_exhausted' ? t('lite.trialExhausted') : t('lite.limitReached')}
            </span>
            {isAdmin ? (
              <Link to="/billing">
                <Button size="sm" variant="secondary">
                  {t('lite.goToBilling')}
                </Button>
              </Link>
            ) : (
              <span className="text-xs text-muted-foreground">{t('lite.askAdmin')}</span>
            )}
          </div>
        </Alert>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <KpiCard
          title={t('dashboard.kpi.active')}
          value={kpis.active}
          delta={usageHint ?? t('dashboard.kpi.noData')}
          spark={[3, 4, 3, 5, 4, 6, 5]}
        />
        <KpiCard
          title={t('dashboard.kpi.etaWeek')}
          value={kpis.etaWeek}
          delta={t('dashboard.kpi.noData')}
          spark={[2, 3, 2, 4, 3, 3, 4]}
        />
        <KpiCard
          title={t('dashboard.kpi.pickup')}
          value={kpis.pickup}
          delta={t('dashboard.kpi.noData')}
          spark={[0, 1, 1, 2, 1, 2, 2]}
        />
      </div>

      {/* Reiter „Aktiv" / „Abgeschlossen" (EPIC Q / ADR-041) — Segmented-Buttons über dem Tabellenbereich. */}
      <div className="flex gap-2">
        <Button
          variant={tab === 'active' ? 'default' : 'secondary'}
          size="sm"
          onClick={() => setTab('active')}
          data-testid="tab-active"
        >
          {t('archive.tabActive')}
        </Button>
        <Button
          variant={tab === 'completed' ? 'default' : 'secondary'}
          size="sm"
          onClick={() => setTab('completed')}
          data-testid="tab-completed"
        >
          {t('archive.tabCompleted')}
        </Button>
      </div>

      {tab === 'completed' ? (
        archiveLoading && archived.length === 0 ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : (
          <ArchiveTable
            containers={archived}
            fmtDate={fmtDate}
            token={token}
            onReactivate={(c) => void onReactivate(c)}
            onRequestDelete={(c) => setPendingDelete(c)}
            canManage={canDelete}
            busyId={reactivatingId ?? deletingId}
          />
        )
      ) : (
        <>
      {/* Eigene Premium-Container-Tabelle ÜBER der Standard-Tabelle (D7). */}
      <PremiumTable
        containers={premium}
        flashedIds={flashedIds}
        fmtDate={fmtDate}
        token={token}
        depots={depots}
        depotSel={depotSel}
        depotResult={depotResult}
        depotChecking={depotChecking}
        onDepotSelect={(c, depotId, reference) =>
          void runDepotCheck(c.trackedContainerId, c.containerNumber, depotId, reference)
        }
        onRequestDelete={(c) => setPendingDelete(c)}
        canDelete={canDelete}
        deletingId={deletingId}
        onRequestComplete={(c) => void onCompleteManual(c)}
        completingId={completingId}
      />

      <Card>
        <CardHeader className="flex-row flex-wrap items-center justify-between gap-3">
          <CardTitle>{t('dashboard.containers')}</CardTitle>
          <div className="flex items-center gap-3">
            <Button
              variant={onlyMine ? 'default' : 'secondary'}
              size="sm"
              onClick={() => setOnlyMine((v) => !v)}
              title={t('dashboard.onlyMine')}
            >
              <User className="h-4 w-4" />
              {t('dashboard.onlyMine')}
            </Button>
            {usageHint && <span className="text-xs text-muted-foreground">{usageHint}</span>}
          </div>
        </CardHeader>
        <CardContent>
          {standard.length === 0 ? (
            <EmptyState
              title={t('dashboard.noContainers')}
              hint={t('dashboard.noContainersHint')}
              action={
                <Link to="/tracking">
                  <Button variant="secondary">{t('dashboard.startTracking')}</Button>
                </Link>
              }
            />
          ) : (
            <Table>
              <THead>
                <TR>
                  <TH>{t('dashboard.col.number')}</TH>
                  <TH>{t('dashboard.col.carrier')}</TH>
                  <TH>{t('dashboard.col.vesselPort')}</TH>
                  <TH>{t('dashboard.col.eta')}</TH>
                  <TH>{t('dashboard.col.status')}</TH>
                  <TH>{t('dashboard.col.updated')}</TH>
                  <TH>{t('dashboard.col.createdBy')}</TH>
                  <TH>{t('dashboard.col.depot')}</TH>
                  <TH className="text-right">{t('dashboard.col.action')}</TH>
                </TR>
                <TR className="[&>th]:pb-2 [&>th]:pt-0 hover:bg-transparent">
                  <TH>
                    <FilterInput
                      value={filters.number}
                      onChange={(v) => setFilters((f) => ({ ...f, number: v }))}
                      placeholder={t('dashboard.filterPlaceholder')}
                    />
                  </TH>
                  <TH>
                    <FilterInput
                      value={filters.carrier}
                      onChange={(v) => setFilters((f) => ({ ...f, carrier: v }))}
                      placeholder={t('dashboard.filterPlaceholder')}
                    />
                  </TH>
                  <TH>
                    <FilterInput
                      value={filters.vesselPort}
                      onChange={(v) => setFilters((f) => ({ ...f, vesselPort: v }))}
                      placeholder={t('dashboard.filterPlaceholder')}
                    />
                  </TH>
                  <TH>
                    <FilterInput
                      value={filters.eta}
                      onChange={(v) => setFilters((f) => ({ ...f, eta: v }))}
                      placeholder={t('dashboard.filterPlaceholder')}
                    />
                  </TH>
                  <TH />
                  <TH>
                    <FilterInput
                      value={filters.updated}
                      onChange={(v) => setFilters((f) => ({ ...f, updated: v }))}
                      placeholder={t('dashboard.filterPlaceholder')}
                    />
                  </TH>
                  <TH>
                    <FilterInput
                      value={filters.createdBy}
                      onChange={(v) => setFilters((f) => ({ ...f, createdBy: v }))}
                      placeholder={t('dashboard.filterPlaceholder')}
                    />
                  </TH>
                  <TH />
                  <TH />
                </TR>
              </THead>
              <TBody>
                {visibleContainers.length === 0 ? (
                  <TR>
                    <TD colSpan={9} className="py-6 text-center text-muted-foreground">
                      {t('dashboard.noFilterMatch')}
                    </TD>
                  </TR>
                ) : (
                  visibleContainers.map((c) => (
                    <TR key={c.trackedContainerId} className="[&>td]:py-1.5">
                      <TD className="font-mono">
                        {c.containerNumber}
                        {c.sizeType && (
                          <span
                            className="ml-1.5 text-xs text-muted-foreground"
                            title={c.isoType ? `ISO ${c.isoType}` : undefined}
                          >
                            {c.sizeType}
                          </span>
                        )}
                      </TD>
                      <TD>
                        <span
                          className="block max-w-[150px] truncate"
                          title={c.carrierName ?? c.carrierCode ?? undefined}
                        >
                          {c.carrierName ?? c.carrierCode ?? '—'}
                        </span>
                      </TD>
                      <TD>
                        <span
                          className="block max-w-[170px] truncate"
                          title={[c.vessel, c.port].filter(Boolean).join(' · ') || undefined}
                        >
                          {c.vessel ?? '—'}
                          {c.port && <span className="text-muted-foreground"> · {c.port}</span>}
                        </span>
                      </TD>
                      <TD>{fmtDate(c.eta)}</TD>
                      <TD>
                        {c.hasData ? (
                          <div className="flex items-center gap-1">
                            <StatusChip
                              letter={t('dashboard.st1.discharge')}
                              title={`${t('lite.fields.discharge')}: ${t('status.' + c.status.discharge)}`}
                              flag={c.status.discharge}
                            />
                            <StatusChip
                              letter={t('dashboard.st1.release')}
                              title={`${t('lite.fields.release')}: ${t('status.' + c.status.release)}`}
                              flag={c.status.release}
                            />
                            <StatusChip
                              letter={t('dashboard.st1.customs')}
                              title={`${t('lite.fields.customs')}: ${t('status.' + c.status.customs)}`}
                              flag={c.status.customs}
                            />
                            <StatusChip
                              letter={t('dashboard.st1.block')}
                              title={`${t('lite.fields.block')}: ${t('status.' + c.status.block)}`}
                              flag={c.status.block}
                            />
                            <StatusChip
                              letter={t('dashboard.st1.pickup')}
                              title={`${t('lite.fields.pickup')}: ${t('status.' + c.status.pickup)}`}
                              flag={c.status.pickup}
                            />
                          </div>
                        ) : (
                          <span className="text-xs text-muted-foreground">
                            {t('dashboard.notFetched')}
                          </span>
                        )}
                      </TD>
                      <TD className="whitespace-nowrap text-muted-foreground">
                        {fmtDate(c.lastRefreshedAt)}
                      </TD>
                      <TD>
                        {c.createdByName ? (
                          <Badge variant="default" title={c.createdByName}>
                            {initials(c.createdByName)}
                          </Badge>
                        ) : (
                          <span className="text-muted-foreground">—</span>
                        )}
                      </TD>
                      <TD>
                        <DepotCell
                          depots={depots}
                          selectedId={depotSel[c.trackedContainerId]}
                          result={depotResult[c.trackedContainerId]}
                          checking={!!depotChecking[c.trackedContainerId]}
                          onSelect={(depotId, reference) =>
                            void runDepotCheck(
                              c.trackedContainerId,
                              c.containerNumber,
                              depotId,
                              reference,
                            )
                          }
                        />
                      </TD>
                      <TD className="text-right">
                        <div className="flex items-center justify-end gap-1.5">
                          <PremiumActivateButton
                            containerNumber={c.containerNumber}
                            billingMode={premiumMode}
                            token={token}
                            onActivated={() => void reloadAll()}
                          />
                          <Button
                            variant="secondary"
                            size="icon"
                            title={t('dashboard.refresh')}
                            aria-label={t('dashboard.refresh')}
                            disabled={refreshingId === c.trackedContainerId}
                            onClick={() => void onRefresh(c.trackedContainerId)}
                          >
                            {refreshingId === c.trackedContainerId ? (
                              <Spinner />
                            ) : (
                              <RefreshCw className="h-4 w-4" />
                            )}
                          </Button>
                          {canDelete(c) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              title={t('archive.completeAction')}
                              aria-label={t('archive.completeAction')}
                              disabled={completingId === c.trackedContainerId}
                              onClick={() => void onCompleteManual(c)}
                              className="text-muted-foreground"
                            >
                              {completingId === c.trackedContainerId ? (
                                <Spinner />
                              ) : (
                                <Archive className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                          {canDelete(c) && (
                            <Button
                              variant="ghost"
                              size="icon"
                              title={t('dashboard.delete')}
                              aria-label={t('dashboard.delete')}
                              disabled={deletingId === c.trackedContainerId}
                              onClick={() => setPendingDelete(c)}
                              className="text-muted-foreground hover:text-destructive"
                            >
                              {deletingId === c.trackedContainerId ? (
                                <Spinner />
                              ) : (
                                <Trash2 className="h-4 w-4" />
                              )}
                            </Button>
                          )}
                        </div>
                      </TD>
                    </TR>
                  ))
                )}
              </TBody>
            </Table>
          )}
          <p className="mt-3 text-xs text-muted-foreground">{t('dashboard.refreshHint')}</p>
        </CardContent>
      </Card>
        </>
      )}

      {/* Auto-abgeschlossene Reise reaktivieren = NEUE Abfrage (EPIC Q): Bestätigung mit Kosten-Hinweis. */}
      <Modal
        open={!!pendingReactivate}
        onClose={() => setPendingReactivate(null)}
        size="sm"
        title={t('archive.requeryTitle')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setPendingReactivate(null)}>
              {t('dashboard.cancel')}
            </Button>
            <Button
              onClick={() => {
                const number = pendingReactivate?.containerNumber;
                setPendingReactivate(null);
                if (number) navigate(`/tracking?number=${encodeURIComponent(number)}`);
              }}
            >
              {t('archive.requeryConfirm')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-muted-foreground">
          {t('archive.requeryBody', { number: pendingReactivate?.containerNumber ?? '' })}
        </p>
      </Modal>

      <Modal
        open={!!pendingDelete}
        onClose={() => !deletingId && setPendingDelete(null)}
        size="sm"
        title={t('dashboard.deleteConfirmTitle')}
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setPendingDelete(null)}
              disabled={!!deletingId}
            >
              {t('dashboard.cancel')}
            </Button>
            <Button
              variant="destructive"
              onClick={() => pendingDelete && void onDelete(pendingDelete)}
              disabled={!!deletingId}
            >
              {deletingId ? <Spinner /> : t('dashboard.delete')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-muted-foreground">
          {t('dashboard.deleteConfirmBody', { number: pendingDelete?.containerNumber ?? '' })}
        </p>
      </Modal>
    </div>
  );
}
